import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { pinnedViewLeft } from "../lib/chatWidth.js";
import {
  RAIL_DEFAULT_SECTION,
  clampRailPanelWidth,
  nextRailState,
  railTourFlashSection,
  RAIL_WIDTH_KEY,
  setSectionOpen,
} from "../lib/navRail.js";

/**
 * Which rail section is showing.
 *
 * The Forecasts and Chat panels share one panel: `logOpen` / `chatOpen` and their setters are
 * DERIVED from a single active section, so every reader and caller (the tour, Shift+Arrow,
 * drawer help, panelActions) keeps working while the two can never be open together.
 *
 * @param {object} deps
 * @param {boolean} deps.contractOpen   the Resolution Criteria popup, which has its own state
 * @param {Function} deps.setContractOpen
 */
export function useNavRailSections({ contractOpen, setContractOpen }) {
  // The forecast gets the full width and chat is one click away, on a first visit too: the
  // guided tour that narrated in the chat is retired.
  const [rail, setRail] = useState(() => ({
    active: null,
    last: RAIL_DEFAULT_SECTION,
    switched: false,
  }));

  const setRailLogOpen = useCallback((v) => setRail((p) => setSectionOpen(p, "forecasts", v)), []);
  const setRailChatOpen = useCallback((v) => setRail((p) => setSectionOpen(p, "chat", v)), []);
  /** A rail icon: open its section, or collapse the panel if it is already showing. */
  const selectSection = useCallback(
    (id) => setRail((p) => nextRailState(p, p.active === id ? null : id)),
    [],
  );
  /** The collapse control: hide the panel, or bring back the section that was showing. */
  const toggleCollapse = useCallback(
    () => setRail((p) => nextRailState(p, p.active ? null : p.last)),
    [],
  );

  return {
    logOpen: rail.active === "forecasts",
    chatOpen: rail.active === "chat",
    contractOpen,
    setLogOpen: setRailLogOpen,
    setChatOpen: setRailChatOpen,
    setContractOpen,
    activeSection: rail.active,
    lastSection: rail.last,
    switchedSection: rail.switched,
    selectSection,
    toggleCollapse,
  };
}

const readWidth = (min, uiZoom) => {
  try {
    const viewportW = typeof window !== "undefined" ? window.innerWidth / uiZoom : Infinity;
    const saved = Number(localStorage.getItem(RAIL_WIDTH_KEY));
    return saved ? clampRailPanelWidth(saved, min, viewportW) : min;
  } catch {
    return min;
  }
};

/**
 * The rail panel's width — one width shared by every section, so resizing one resizes all —
 * drag-resizable from its RIGHT edge, up
 * to a third of the screen. Mirrors useChatWidth (no re-render while dragging: the width lives in
 * the `--rail-panel-w` CSS variable and the timeline pans so the day at the panel's edge
 * stays put; on release it is committed, saved, and the timeline re-framed through
 * `reframeRef`), with the drag direction flipped for a left-side panel.
 *
 * @param {object} deps
 * @param {number} deps.minWidth  the floor: the widest section's minimum, so none is squeezed
 * @param {{current: number}} deps.vlRef
 * @param {{current: number}} deps.ppdRef
 * @param {() => void} deps.scheduleRender
 * @param {number} deps.uiZoom
 */
export function useNavRailPanelWidth({ minWidth, vlRef, ppdRef, scheduleRender, uiZoom = 1 }) {
  const min = minWidth;
  const [saved, setSaved] = useState(null);
  // Clamped on every read, so a width saved under an older, wider cap comes back in bounds.
  const width = clampRailPanelWidth(
    saved ?? readWidth(min, uiZoom),
    min,
    typeof window !== "undefined" ? window.innerWidth / uiZoom : Infinity,
  );
  const reframeRef = useRef(null);
  const [reframeToken, setReframeToken] = useState(0);
  const dragRef = useRef(null);
  const endDragRef = useRef(null);

  const setCssWidth = (w) => {
    document.documentElement.style.setProperty("--rail-panel-w", `${w}px`);
  };
  useLayoutEffect(() => {
    setCssWidth(width);
  }, [width]);
  useEffect(() => {
    if (reframeToken) reframeRef.current?.();
  }, [reframeToken]);
  useEffect(() => () => endDragRef.current?.(), []);

  const applyLive = useCallback(
    (w) => {
      const d = dragRef.current;
      const prev = d ? d.prevW : width;
      if (w === prev) return;
      setCssWidth(w);
      // The panel's edge moves RIGHT as it widens, so the pin runs the other way round.
      vlRef.current = pinnedViewLeft(vlRef.current, ppdRef.current, w, prev);
      if (d) d.prevW = w;
      scheduleRender();
    },
    [width, vlRef, ppdRef, scheduleRender],
  );

  const commit = useCallback((w) => {
    try {
      localStorage.setItem(RAIL_WIDTH_KEY, String(w));
    } catch {
      /* storage unavailable — the width just won't persist */
    }
    setSaved(w);
    setReframeToken((n) => n + 1);
  }, []);

  const viewportCss = () => (typeof window !== "undefined" ? window.innerWidth / uiZoom : Infinity);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { startX: e.clientX, startW: width, prevW: width };
    document.body.classList.add("chat-resizing");
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      // Dragging RIGHT widens a left-side panel.
      applyLive(
        clampRailPanelWidth(d.startW + (ev.clientX - d.startX) / uiZoom, min, viewportCss()),
      );
    };
    const end = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      window.removeEventListener("blur", end);
      document.body.classList.remove("chat-resizing");
      endDragRef.current = null;
      const d = dragRef.current;
      dragRef.current = null;
      if (d && d.prevW !== d.startW) commit(d.prevW);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    window.addEventListener("blur", end);
    endDragRef.current = end;
  };
  const onDoubleClick = () => {
    if (width === min) return;
    dragRef.current = { startX: 0, startW: width, prevW: width };
    applyLive(min);
    dragRef.current = null;
    commit(min);
  };

  return { panelWidth: width, reframeRef, handleProps: { onPointerDown, onDoubleClick } };
}

/** How long the tour waits in the Forecast Manager before pointing back at Chat. */
export const RAIL_TOUR_RETURN_HINT_MS = 6000;

/** How long the user may stay in any other panel mid-tour before Chat flashes. */
export const RAIL_TOUR_AWAY_HINT_MS = 20000;

/**
 * Which rail icon the tour flashes, in the navigation-rail layout. Its "explore" step flashes
 * Forecasts until the user opens it; six seconds in, Chat flashes until they come back. A tour
 * step narrated while the Chat Console is not showing flashes Chat until it is opened, and so
 * does staying in another panel for twenty seconds.
 *
 * @param {object} deps
 * @param {boolean} deps.enabled      the rail is on and the tour is running
 * @param {string|null} deps.tourSpotlight
 * @param {string|null} deps.tourWaitKey
 * @param {boolean} deps.logOpen
 * @param {boolean} deps.chatOpen
 * @param {boolean} deps.contractOpen
 * @param {number} deps.tourSayCount  bumped on every tour narration line
 * @returns {"forecasts"|"chat"|null}
 */
export function useRailTourFlash({
  enabled,
  tourSpotlight,
  tourWaitKey,
  logOpen,
  chatOpen = false,
  contractOpen = false,
  tourSayCount = 0,
}) {
  const [returnHintDue, setReturnHintDue] = useState(false);
  // Narration lines the user has had the chance to read: caught up whenever Chat is showing
  // (or the tour is off), so closing Chat after reading never raises the flag.
  const [seenSayCount, setSeenSayCount] = useState(tourSayCount);
  if ((chatOpen || !enabled) && seenSayCount !== tourSayCount) setSeenSayCount(tourSayCount);
  const unseenTourStep = enabled && !chatOpen && tourSayCount > seenSayCount;
  const inManager = enabled && tourWaitKey === "panels_settled" && logOpen;
  useEffect(() => {
    if (!inManager) return;
    const t = setTimeout(() => setReturnHintDue(true), RAIL_TOUR_RETURN_HINT_MS);
    return () => {
      clearTimeout(t);
      setReturnHintDue(false);
    };
  }, [inManager]);
  const [awayTooLong, setAwayTooLong] = useState(false);
  const inOtherPanel = enabled && !chatOpen && (logOpen || contractOpen);
  useEffect(() => {
    if (!inOtherPanel) return;
    const t = setTimeout(() => setAwayTooLong(true), RAIL_TOUR_AWAY_HINT_MS);
    return () => {
      clearTimeout(t);
      setAwayTooLong(false);
    };
  }, [inOtherPanel]);
  return railTourFlashSection({
    enabled,
    tourSpotlight,
    logOpen,
    inManager,
    returnHintDue,
    unseenTourStep,
    awayTooLong,
  });
}
